// User Level Constants
export const USER_LEVELS = {
  ADMIN: 0,
  BUSINESS_OWNER: 2,
  CUSTOMER: 3,
};

// User Level Labels
export const USER_LEVEL_LABELS = {
  [USER_LEVELS.ADMIN]: "Admin",
  [USER_LEVELS.BUSINESS_OWNER]: "Business Owner",
  [USER_LEVELS.CUSTOMER]: "Customer",
};
export const USER_LEVEL_LABEL_KEYS = {
  [USER_LEVELS.ADMIN]: "customers.levels.admin",
  [USER_LEVELS.BUSINESS_OWNER]: "customers.levels.businessOwner",
  [USER_LEVELS.CUSTOMER]: "customers.levels.customer",
};

// User Status Colors (Centralized)
export const USER_STATUS_COLORS = {
  ACTIVE: {
    text: "text-green-400",
    bg: "bg-green-500/20",
    border: "border-green-500/20",
    button: "bg-green-600 hover:bg-green-700",
    full: "bg-green-500/10 text-green-400 border border-green-500/20",
  },
  INACTIVE: {
    text: "text-red-400",
    bg: "bg-red-500/20",
    border: "border-red-500/20",
    button: "bg-red-600 hover:bg-red-700",
    full: "bg-red-500/10 text-red-400 border border-red-500/20",
  },
};

// User Card Themes
export const USER_CARD_THEMES = {
  ACTIVE: {
    card: "bg-gray-900 border-green-500/30 hover:border-green-500/50",
    avatar: "from-green-500 to-emerald-500",
    name: "text-white",
    role: "text-green-400",
    contact: "text-gray-300",
    stats: {
      label: "text-gray-400",
      value: "text-white",
      wallet: "text-green-400",
    },
  },
  INACTIVE: {
    card: "bg-gray-800/50 border-red-500/30 hover:border-red-500/50",
    avatar: "from-red-500 to-pink-500",
    name: "text-gray-300",
    role: "text-red-400",
    contact: "text-gray-500",
    stats: {
      label: "text-gray-500",
      value: "text-gray-400",
      wallet: "text-red-400",
    },
  },
};

// User Level Colors
export const USER_LEVEL_COLORS = {
  [USER_LEVELS.ADMIN]: "text-purple-400", // Changed from red to purple
  [USER_LEVELS.BUSINESS_OWNER]: "text-blue-400",
  [USER_LEVELS.CUSTOMER]: "text-green-400",
};

// User Level Badge Colors
export const USER_LEVEL_BADGE_COLORS = {
  [USER_LEVELS.ADMIN]:
    "bg-purple-500/10 text-purple-400 border border-purple-500/20", // Changed from red to purple
  [USER_LEVELS.BUSINESS_OWNER]:
    "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  [USER_LEVELS.CUSTOMER]:
    "bg-green-500/10 text-green-400 border border-green-500/20",
};

// User Type Configuration with enabled flag
export const USER_TYPE_CONFIG = {
  customers: {
    key: "customers",
    label: "Customers",
    labelKey: "tabs.customers",
    level: USER_LEVELS.CUSTOMER,
    color: "text-green-400",
    enabled: true,
  },
  "business-owners": {
    key: "business-owners",
    label: "Business Owners",
    labelKey: "tabs.businessOwners",
    level: USER_LEVELS.BUSINESS_OWNER,
    color: "text-blue-400",
    enabled: false, // Hidden as requested
  },
  admins: {
    key: "admins",
    label: "Admins",
    labelKey: "tabs.admins",
    level: USER_LEVELS.ADMIN,
    color: "text-purple-400",
    enabled: true,
  },
};

// Filter Tabs Configuration - only enabled types
export const USER_FILTER_TABS = Object.values(USER_TYPE_CONFIG)
  .filter((type) => type.enabled)
  .map((type) => ({
    key: type.key,
    label: type.label,
    level: type.level,
    color: type.color,
  }));

// Helper function to get enabled user types
export const getEnabledUserTypes = () => {
  return Object.keys(USER_TYPE_CONFIG).filter(
    (key) => USER_TYPE_CONFIG[key].enabled,
  );
};

// Helper function to check if a user type is enabled
export const isUserTypeEnabled = (userType) => {
  return USER_TYPE_CONFIG[userType]?.enabled || false;
};

// Helper function to check if wallet is enabled based on configs
export const isWalletEnabledFromConfigs = (configs) => {
  if (!configs) return false;
  return configs?.wallet_cash_enabled?.value === "1";
};

// Tab visibility configuration for user detail page
export const USER_DETAIL_TABS = {
  overview: {
    id: "overview",
    label: "Overview",
    labelKey: "detail.tabs.overview",
    enabled: true, // Always enabled
    requiredLevels: [], // Available for all levels
  },
  "order-history": {
    id: "order-history",
    label: "Order History",
    labelKey: "detail.tabs.orderHistory",
    enabled: true,
    // requiredLevels: [USER_LEVELS.CUSTOMER, USER_LEVELS.BUSINESS_OWNER] // Not for admins
    requiredLevels: [], // Available for all levels
  },
  wallets: {
    id: "wallets",
    label: "Wallets",
    labelKey: "detail.tabs.wallets",
    enabled: false, // Dynamic based on configs
    // requiredLevels: [USER_LEVELS.CUSTOMER, USER_LEVELS.BUSINESS_OWNER] // Not for admins
    requiredLevels: [], // Available for all levels
  },
  "loyalty-points": {
    id: "loyalty-points",
    label: "Loyalty Points",
    labelKey: "detail.tabs.loyaltyPoints",
    enabled: false,
    // requiredLevels: [USER_LEVELS.CUSTOMER, USER_LEVELS.BUSINESS_OWNER] // Not for admins
    requiredLevels: [], // Available for all levels
  },
  addresses: {
    id: "addresses",
    label: "Addresses",
    labelKey: "detail.tabs.addresses",
    enabled: true,
    requiredLevels: [], // Available for all levels
  },
};

// Helper function to get visible tabs for a user level
export const getVisibleTabsForUserLevel = (userLevel, configs = null) => {
  return Object.values(USER_DETAIL_TABS).filter((tab) => {
    // Call enabled as function if it is one, otherwise use boolean value
    const isEnabled =
      typeof tab.enabled === "function" ? tab.enabled(configs) : tab.enabled;
    return (
      isEnabled &&
      (tab.requiredLevels.length === 0 ||
        tab.requiredLevels.includes(userLevel))
    );
  });
};

// Helper function to check if a tab should be visible for a user level
export const isTabVisibleForUserLevel = (tabId, userLevel, configs = null) => {
  const tab = USER_DETAIL_TABS[tabId];
  if (!tab) return false;

  // Call enabled as function if it is one, otherwise use boolean value
  const isEnabled =
    typeof tab.enabled === "function" ? tab.enabled(configs) : tab.enabled;
  if (!isEnabled) return false;

  return (
    tab.requiredLevels.length === 0 || tab.requiredLevels.includes(userLevel)
  );
};

// Enabled Filter Options
export const ENABLED_FILTER_OPTIONS = [
  { value: "all", label: "All Users", labelKey: "filters.allUsers" },
  { value: "enabled", label: "Active", labelKey: "filters.active" },
  { value: "disabled", label: "Inactive", labelKey: "filters.inactive" },
];

export const USER_STATUS_FILTER_TABS = [
  {
    key: "enabled",
    color: "text-green-400",
  },
  {
    key: "disabled",
    color: "text-red-400",
  },
  {
    key: "all",
    color: "text-white",
  },
];

// Pagination Constants
export const ITEMS_PER_PAGE = 15;
export const MAX_VISIBLE_PAGES = 5;

// Default Parameters to Send
export const paramsToSend = {
  params:
    "created_at,name,lastname,email,phone,photo,cellphone,country_phone_code,location,level,enabled,middle_name,second_lastname,birthdate,phone_verified,email_verified,orders_count,wallets,addresses",
  page_size: ITEMS_PER_PAGE,
  page: 1,
  orderBy: "-id",
};

// Utility Functions
export const getUserLevelLabel = (level) => {
  return USER_LEVEL_LABELS[level] || "Unknown";
};

export const getUserLevelColor = (level) => {
  return USER_LEVEL_COLORS[level] || "text-gray-400";
};

export const getUserLevelBadgeColor = (level) => {
  return (
    USER_LEVEL_BADGE_COLORS[level] ||
    "bg-gray-500/10 text-gray-400 border border-gray-500/20"
  );
};

export const getUserName = (user) => {
  return (
    [user?.name, user?.lastname].filter(Boolean).join(" ").trim() || "User"
  );
};

export const getUserPhone = (user, returnValue) => {
  const countryPhoneCode = user?.country_phone_code
    ? `+${user?.country_phone_code}`
    : "";
  return (
    [countryPhoneCode, user?.cellphone].filter(Boolean).join(" ").trim() ||
    (returnValue ?? "No data")
  );
};

export const getUserEmail = (user) => {
  return user?.email || "No email";
};

export const getUserInitials = (user) => {
  const name = getUserName(user);

  // Limpiar espacios múltiples y dividir por espacios
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);

  if (parts.length >= 2) {
    // Tomar la primera letra del primer nombre y primer apellido
    const firstInitial = parts[0][0]?.toUpperCase() || "";
    const secondInitial = parts[1][0]?.toUpperCase() || "";
    return `${firstInitial}${secondInitial}`;
  }

  // Si solo hay un nombre, usar la primera letra
  if (parts.length === 1 && parts[0].length > 0) {
    return parts[0][0]?.toUpperCase() || "U";
  }

  // Fallback si no hay nombre válido
  return "U";
};

export const getUserWalletBalance = (user) => {
  if (!user?.wallets || !Array.isArray(user.wallets)) return 0;

  const cashWallet = user.wallets.find((wallet) => wallet.type === "cash");
  return cashWallet ? parseFloat(cashWallet.balance) : 0;
};

export const getUserOrdersCount = (user) => {
  return user?.orders_count || 0;
};

export const getUserStatusBadge = (enabled) => {
  return enabled ? "Active" : "Inactive";
};
export const getUserStatusBadgeKey = (enabled) => {
  return enabled ? "common.states.active" : "common.states.inactive";
};

export const getUserStatusColor = (enabled) => {
  return enabled
    ? USER_STATUS_COLORS.ACTIVE.text
    : USER_STATUS_COLORS.INACTIVE.text;
};

export const getUserStatusBadgeColor = (enabled) => {
  return enabled
    ? USER_STATUS_COLORS.ACTIVE.full
    : USER_STATUS_COLORS.INACTIVE.full;
};

export const getUserCardTheme = (enabled) => {
  return enabled ? USER_CARD_THEMES.ACTIVE : USER_CARD_THEMES.INACTIVE;
};

// Build Where Clause for API
export const buildUserWhereClause = (
  userType,
  enabledFilter = "all",
  searchTerm = "",
) => {
  const baseWhere = {
    conditions: [],
    conector: "AND",
  };

  // Add user level condition - only if user type is enabled
  if (userType !== "all" && isUserTypeEnabled(userType)) {
    let level;
    switch (userType) {
      case "customers":
        level = USER_LEVELS.CUSTOMER; // 3
        break;
      case "business-owners":
        level = USER_LEVELS.BUSINESS_OWNER; // 2
        break;
      case "admins":
        level = USER_LEVELS.ADMIN; // 0
        break;
      default:
        level = undefined;
    }

    if (level !== undefined) {
      baseWhere.conditions.push({
        attribute: "level",
        value: [level],
      });
    }
  }

  // Add enabled condition
  if (enabledFilter !== "all") {
    const enabled = enabledFilter === "enabled";
    baseWhere.conditions.push({
      attribute: "enabled",
      value: enabled,
    });
  }

  // Add search condition
  if (searchTerm && searchTerm.trim()) {
    const searchValue = `%${searchTerm.trim()}%`;
    baseWhere.conditions.push({
      conector: "OR",
      conditions: [
        {
          attribute: "name",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
        {
          attribute: "lastname",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
        {
          attribute: "email",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
        {
          attribute: "cellphone",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
      ],
    });
  }

  return baseWhere;
};
